<template>
  <canvas v-show="visible" ref="cv" class="dsh-cursor" :style="cvStyle"></canvas>
</template>

<script setup>
/* 全站自定义鼠标指针
 * - 指针/大小来自站点 setting（后台「页面管理 → 鼠标指针」）
 * - 可以勾选多个：每次刷新随机出现一个
 * - 后台一保存立刻更换（localStorage + BroadcastChannel，不用手动刷新）
 * - 后台页面也生效；只有触屏设备跳过
 */
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { parseCursorFile, contentBox } from '../../util/aniCursor';
import { fetchSiteSetting, onSettingsChanged, parsePicks, pickRandom } from '../../util/settingsBus';

const cv = ref(null);
const visible = ref(false);

// 这几个必须是响应式的，否则尺寸算出来是「首次渲染那一下」的旧值（改大小没反应就是这么来的）
const natW = ref(32);
const natH = ref(32);
const scale = ref(1);
const crop = ref({ x: 0, y: 0, w: 32, h: 32 });

let frames = [];
let rates = [];
let hotspot = { x: 0, y: 0 };
let loadedKey = '';
let wantedSize = 32;
let frameIdx = 0;
let frameAt = 0;
let rafId = 0;
let lastX = -100;
let lastY = -100;
let movePending = false;
let offSettings = null;
let active = false;
let destroyed = false;

const cvStyle = computed(() => ({
  width: Math.round(natW.value * scale.value) + 'px',
  height: Math.round(natH.value * scale.value) + 'px'
}));

const drawFrame = () => {
  const el = cv.value;
  if (!el || !frames.length) return;
  const ctx = el.getContext('2d');
  const c = crop.value;
  ctx.clearRect(0, 0, el.width, el.height);
  ctx.drawImage(frames[frameIdx % frames.length], -c.x, -c.y);
};

const tick = (now) => {
  if (!frames.length) return;
  const rate = rates[frameIdx % rates.length] || 6;
  const dur = Math.max(20, (rate * 1000) / 60);
  if (now - frameAt >= dur) {
    frameAt = now - ((now - frameAt) % dur);
    frameIdx = (frameIdx + 1) % frames.length;
    drawFrame();
  }
  rafId = requestAnimationFrame(tick);
};

const place = () => {
  const el = cv.value;
  if (!el) return;
  const x = Math.round(lastX - hotspot.x * scale.value);
  const y = Math.round(lastY - hotspot.y * scale.value);
  el.style.transform = 'translate3d(' + x + 'px,' + y + 'px,0)';
};

const onMove = (e) => {
  lastX = e.clientX;
  lastY = e.clientY;
  if (!visible.value) visible.value = true;
  if (!movePending) {
    movePending = true;
    requestAnimationFrame(() => { movePending = false; place(); });
  }
};
const onLeave = () => { visible.value = false; };

/** 只要大小变了，不用重新下载文件：直接改缩放 */
const applySize = (size) => {
  wantedSize = Number(size) || 32;
  scale.value = wantedSize / Math.max(crop.value.w || 32, crop.value.h || 32);
  place();
};

const teardown = () => {
  active = false;
  loadedKey = '';
  frames = [];
  rates = [];
  visible.value = false;
  document.documentElement.classList.remove('dsh-cursor-on');
  if (window.__dshCursor) delete window.__dshCursor;
  if (rafId) { cancelAnimationFrame(rafId); rafId = 0; }
  document.removeEventListener('mousemove', onMove);
  document.removeEventListener('mouseleave', onLeave);
  document.removeEventListener('mouseenter', onMove);
};

const start = async (key, size) => {
  try {
    const res = await fetch('/cursor/' + key.split('/').map(encodeURIComponent).join('/'));
    if (!res.ok) throw new Error('HTTP ' + res.status);
    const buf = await res.arrayBuffer();

    const parsed = await parseCursorFile(buf, key);
    frames = parsed.frames;
    rates = parsed.rates && parsed.rates.length ? parsed.rates : [60000];
    hotspot = parsed.hotspot || { x: 0, y: 0 };

    const box = contentBox(frames[0]);
    crop.value = box;
    natW.value = box.w;
    natH.value = box.h;
    hotspot = { x: hotspot.x - box.x, y: hotspot.y - box.y };
    loadedKey = key;

    const el = cv.value;
    if (!el) return;
    el.width = box.w;
    el.height = box.h;
    applySize(size);
    frameIdx = 0;
    drawFrame();

    document.documentElement.classList.add('dsh-cursor-on');
    document.addEventListener('mousemove', onMove, { passive: true });
    document.addEventListener('mouseleave', onLeave);
    document.addEventListener('mouseenter', onMove);
    if (rafId) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(tick);
    active = true;
    // 排查用：控制台能摸到当前用的是哪个指针
    window.__dshCursor = { key: key, size: wantedSize, scale: scale.value, frames: frames.length };
    return true;
  } catch (e) {
    console.warn('[指针] 加载失败，继续用系统指针：' + key + ' → ' + ((e && e.message) ? e.message : e));
    teardown();
    return false;
  }
};

const apply = async (st, force) => {
  if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) return;
  // 后台页面也要生效（之前这里把 /admin 跳过了，主人在后台看到的还是系统指针）

  const enabled = Number(st && st.cursor_enabled) === 1;
  let picks = parsePicks(st && st.cursor_picks);
  if (!picks.length && st && st.cursor_folder && st.cursor_main) {
    picks = [String(st.cursor_folder) + '/' + String(st.cursor_main)];
  }
  if (!enabled || !picks.length) { teardown(); return; }

  const size = Number(st && st.cursor_size) || 32;
  const first = pickRandom(picks);
  if (!force && first === loadedKey && active) { applySize(size); return; }  // 只改了大小
  teardown();

  // 随机池里可能混着已经删掉的文件，一个个试，别因此没指针
  const order = picks.slice();
  for (let i = order.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const t = order[i]; order[i] = order[j]; order[j] = t;
  }
  if (order[0] !== first) {
    const idx = order.indexOf(first);
    if (idx > 0) { order[idx] = order[0]; order[0] = first; }
  }
  for (const k of order) {
    if (destroyed) return;
    if (await start(k, size)) return;
  }
};

onMounted(async () => {
  const st = await fetchSiteSetting();
  await apply(st);
  offSettings = onSettingsChanged(async () => {
    if (destroyed) return;
    const latest = await fetchSiteSetting();
    // force=false：指纹不变（只是切标签回来）就不重来，免得指针闪一下
    await apply(latest, false);
  });
});

onBeforeUnmount(() => {
  destroyed = true;
  if (offSettings) { offSettings(); offSettings = null; }
  teardown();
});
</script>

<style>
/* 自绘指针生效时把系统指针藏起来（解析失败就不挂这个 class） */
html.dsh-cursor-on,
html.dsh-cursor-on * {
  cursor: none !important;
}
</style>

<style scoped>
.dsh-cursor {
  position: fixed;
  left: 0;
  top: 0;
  z-index: 2147483000;
  pointer-events: none;
  image-rendering: pixelated;
  will-change: transform;
}
</style>
